import { useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Archive,
  ChevronRight,
  Coins,
  FileText,
  Package,
  Route as RouteIcon,
  Truck,
  Users,
} from "lucide-react";
import {
  exportArchivedOrdersReport,
  exportCostSavingsReport,
  exportDriversReport,
  exportFleetLoadReport,
  exportOrdersReport,
  exportRoutesReport,
} from "@/api/reports";
import { Card } from "@/components/ui/Card";
import { CostSavingsReportTable } from "@/features/reports/components/CostSavingsReportTable";
import { OrdersReportTable } from "@/features/reports/components/OrdersReportTable";
import { RatingsSummaryCard } from "@/features/reports/components/RatingsSummaryCard";
import {
  ReportExportCard,
  type ExportFormat,
} from "@/features/reports/components/ReportExportCard";
import { RoutesReportTable } from "@/features/reports/components/RoutesReportTable";
import { useColumnVisibility } from "@/features/reports/hooks/useColumnVisibility";
import { downloadBlob } from "@/lib/downloadBlob";
import { cn } from "@/lib/cn";
import type { ReportParams } from "@/types/reports";

const ROUTE_COLUMNS = [
  { key: "routeName" },
  { key: "date" },
  { key: "vehicle" },
  { key: "driver" },
  { key: "plannedKm" },
  { key: "actualKm" },
  { key: "plannedDur" },
  { key: "actualDur" },
  { key: "stops" },
  { key: "otd" },
  { key: "cost" },
  { key: "utilWeight" },
  { key: "utilVolume" },
];

const ORDER_COLUMNS = [
  { key: "orderName" },
  { key: "customer" },
  { key: "address" },
  { key: "route" },
  { key: "vehicle" },
  { key: "status" },
  { key: "timeWindow" },
  { key: "arrivedAt" },
  { key: "onTime" },
  { key: "rating" },
];

type ReportKind = "routes" | "orders" | "costSavings";

type ExportFn = (
  params: ReportParams & {
    format: ExportFormat;
    lang?: string;
    columns?: string;
  },
) => Promise<Blob>;

/**
 * Shared section-header style for the Reports hub (Sprint 119, TASK-3849 —
 * unify the three previously-divergent header styles into one). Use this for
 * every section heading on this tab so they read identically.
 */
function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
      {children}
    </h2>
  );
}

interface Props {
  /** Shared analytics filter params (date range, vehicle, driver). */
  params: ReportParams;
}

/**
 * Reports export hub (Sprint 117 TASK-3832; de-cluttered in Sprint 119
 * TASK-3846 / TASK-3849).
 *
 * The tab is intentionally short by default (~700–900px instead of ~6800px):
 *
 *  - "Reports for download": the 7 export reports (incl. Driver Statistics)
 *    rendered as compact ~56px list rows, each with a single "Download ▾"
 *    split-button (XLSX / PDF) honouring the shared date-range/vehicle/driver
 *    filter.
 *  - The live recipient-ratings summary, rendered full-width below the list.
 *  - The heavy interactive Routes / Orders / Cost-Savings tables now live behind
 *    a default-COLLAPSED disclosure so they no longer dominate (or overflow on
 *    mobile) the tab — they stay 100% functional once expanded.
 */
export function ReportsHubTab({ params }: Props) {
  const { t, i18n } = useTranslation();
  const [kind, setKind] = useState<ReportKind>("routes");
  const [tablesOpen, setTablesOpen] = useState(false);

  const routeColVisibility = useColumnVisibility(
    "reports_columns_routes",
    ROUTE_COLUMNS,
  );
  const orderColVisibility = useColumnVisibility(
    "reports_columns_orders",
    ORDER_COLUMNS,
  );

  /** Runs an export fn with the shared params + chosen format, then downloads. */
  async function runExport(
    fn: ExportFn,
    slug: string,
    format: ExportFormat,
  ): Promise<void> {
    const blob = await fn({ ...params, format, lang: i18n.language });
    const ext = format === "xlsx" ? "xlsx" : "pdf";
    downloadBlob(blob, `${slug}_${params.date_from}_${params.date_to}.${ext}`);
  }

  const KINDS: { value: ReportKind; label: string; icon: typeof RouteIcon }[] =
    [
      { value: "routes", label: t("reports.tabs.routes"), icon: RouteIcon },
      { value: "orders", label: t("reports.tabs.orders"), icon: Package },
      {
        value: "costSavings",
        label: t("reports.tabs.costSavings"),
        icon: Coins,
      },
    ];

  return (
    <div className="space-y-6">
      {/* "Download" group — compact export list rows, one split-button each. */}
      <div className="space-y-3">
        <SectionHeader>{t("analytics.hub.sectionDownload")}</SectionHeader>
        <Card className="divide-y divide-gray-100">
          <ReportExportCard
            title={t("analytics.hub.driverStats")}
            icon={Users}
            variant="download"
            tint="brand"
            onDownload={(format) =>
              runExport(exportDriversReport, "drivers", format)
            }
          />
          <ReportExportCard
            title={t("analytics.hub.tripSummary")}
            icon={FileText}
            variant="download"
            tint="brand"
            onDownload={(format) =>
              runExport(exportRoutesReport, "routes", format)
            }
          />
          <ReportExportCard
            title={t("analytics.hub.orders")}
            icon={Package}
            variant="download"
            tint="brand"
            onDownload={(format) =>
              runExport(exportOrdersReport, "orders", format)
            }
          />
          <ReportExportCard
            title={t("analytics.hub.costSavings")}
            icon={Coins}
            variant="download"
            tint="emerald"
            onDownload={(format) =>
              runExport(exportCostSavingsReport, "cost-savings", format)
            }
          />
          <ReportExportCard
            title={t("analytics.hub.routeCost")}
            icon={Coins}
            variant="download"
            tint="emerald"
            onDownload={(format) =>
              runExport(exportRoutesReport, "route-cost", format)
            }
          />
          <ReportExportCard
            title={t("analytics.hub.fleetLoad")}
            icon={Truck}
            variant="download"
            tint="brand"
            onDownload={(format) =>
              runExport(exportFleetLoadReport, "fleet-load", format)
            }
          />
          <ReportExportCard
            title={t("analytics.hub.archived")}
            icon={Archive}
            variant="download"
            tint="amber"
            onDownload={(format) =>
              runExport(exportArchivedOrdersReport, "archived-orders", format)
            }
          />
        </Card>
      </div>

      {/* Live recipient-ratings summary — full-width (own internal heading). */}
      <RatingsSummaryCard params={params} />

      {/* Interactive report tables — behind a default-collapsed disclosure. */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setTablesOpen((open) => !open)}
          aria-expanded={tablesOpen}
          className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 transition-colors hover:text-gray-600"
        >
          <ChevronRight
            size={14}
            className={cn("transition-transform", tablesOpen && "rotate-90")}
            aria-hidden
          />
          {t("analytics.hub.openTableDisclosure")}
        </button>

        {tablesOpen && (
          <div className="space-y-3">
            {/* Inner report selector */}
            <div className="overflow-x-auto">
              <div className="flex w-fit items-center gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
                {KINDS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setKind(item.value)}
                      className={cn(
                        "flex shrink-0 items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all",
                        kind === item.value
                          ? "bg-brand-primary text-white shadow-sm"
                          : "text-gray-500 hover:bg-gray-50 hover:text-gray-700",
                      )}
                    >
                      <Icon size={13} />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Table area */}
            <div className="overflow-x-auto">
              {kind === "routes" && (
                <RoutesReportTable
                  params={params}
                  visibleKeys={routeColVisibility.visibleKeys}
                />
              )}
              {kind === "orders" && (
                <OrdersReportTable
                  params={params}
                  visibleKeys={orderColVisibility.visibleKeys}
                />
              )}
              {kind === "costSavings" && (
                <CostSavingsReportTable params={params} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
